import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import find from "lodash/find";
import { RemoveCreditCardRequest, OrderRequest, ClientTokenRequest, ShopCheckoutState, RmsFreightCalcResponse, RmsFreightEstimateType, SelectedShippingMethod, PaymentType, GetShippingMethodMagentoRequest, MagentoOrderRequest, PaymentInformation, CreateOrderResponse, SetShippingMethodMagentoRequest, PaymentInformationGuestCheckout, OrderStatus, OrderStatusResponse, } from "./shop-checkout.interface";
import store, { waitForEstimatedTax } from "../../store/store";
import { getCustomerToken } from "../customer/customer";
import { StoreState } from "../../store/store.interface";
import environment, { isGuestCheckoutPage } from "../../common/environment";
import { PAYMENT_MESSAGE_ID, updateGlobalMessage } from "../alert/global-message";
import { GENERIC_TRANSACTION_ERROR, defaultApiState, MessageTemplateId } from '../../common/constants/api-constants';
import { MessageType, Message } from "../alert/global-message.interface";
import { ActionStatus } from "../../common/constants/action-status";
import { getCart, getGuestCheckoutEmail, getProductCountryCode } from "../cart/shop-cart/store/shop-cart.selectors";
import { RecaptchaAction, getRecaptchaToken } from "../../common/recaptcha";
import { isTunnelTaxSupportedCountry } from "../tax-info/tax-info-options";
import { ApiError, getResponseData } from "../../common/utils/api-utils";
import { freightCalculationMagentoRequest, mapMagentoShippingMethodsResponse, mapMagentoTaxResponse, paymentTaxMagentoRequest } from "./shipping-methods.mapping";
import { isTunnelPurchaseFlow } from "../../common/utils/util";
import { AddressMatchCode } from "../address/interfaces/address-validation.interface";
import { MAGENTO_RECAPTCHA_HEADER, ShopApi, getOrderStatusUrlMagento } from "../../common/constants/shop-api";
import { mapBraintreeMagentoResponse } from "../payment-methods/braintree.mapping";
import { COOKIE_CJE, isCjeCookiePresent, removeCookie } from "../../common/cookies";
import { GuestCheckoutAPI, getCartIdFromURL } from "../../common/constants/guest-checkout-api";
import { getLoggedInCartItemsTotalsMagentoApiCall, getLoggedInCartItemsTotalsMagentoApiCallGuestCheckout } from "../cart/shop-cart/api/shop-cart.api";
import {GetEstimatedTaxParams} from "../estimated-tax/estimated-tax.interface";
import {getEstimatedTaxStatus, getZipCode} from "../estimated-tax/estimated-tax.selectors";
import {getEstimatedTax} from "../estimated-tax/estimated-tax.thunks";
import { updateAddressBeforeSave } from '../../common/utils/address-util';
import OrderResultProcessor from "../order-placement/utils/order-result-processor.util";
import { savePurchaseOrder } from "../cart/shop-cart/thunks/store-purchase-order.thunk";

/*----- DECLARE THUNKS ------*/

export const removeSavedCard = createAsyncThunk('shopcheckout/removeSavedCard', async(removeCreditCardRequest: RemoveCreditCardRequest, thunkAPI) => {
  try{
    const response = await removeVaultedCardApiCallMagento(removeCreditCardRequest, getCustomerToken(store.getState() as StoreState));

    thunkAPI.dispatch(updateVaultedCards(removeCreditCardRequest.updatedVaultedCardsList));
    return response;
  }
  catch(error) {
    thunkAPI.dispatch(updateGlobalMessage({
      id: PAYMENT_MESSAGE_ID,
      message: GENERIC_TRANSACTION_ERROR,
      type: MessageType.primary
    }));
    return thunkAPI.rejectWithValue(error);
  }
},{
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    return state.shopCheckoutState.orderCreation.status !== ActionStatus.loading;
  })
});


export const createOrder = createAsyncThunk('shopcheckout/createOrder', async(orderRequest: OrderRequest | MagentoOrderRequest, thunkAPI) =>{
  try{
    const state = thunkAPI.getState() as StoreState;

    let createOrderResponse: CreateOrderResponse;
    if ('paymentMethod' in orderRequest.order) {
      const customerToken = getCustomerToken(store.getState() as StoreState);
      const guestCheckoutPage = isGuestCheckoutPage();
      if(customerToken && !guestCheckoutPage) {
        // call shipping-information for tax as thats the only api we are sending billing address and tax-details
        const isTunnelTaxSupported = isTunnelTaxSupportedCountry(getProductCountryCode(state));
        if(isTunnelTaxSupported) {
          const taxRequest = paymentTaxMagentoRequest(getBillingAddress(state)!, getBillingAddress(state)!, getShippingMethod(state));
          await calculateTaxMagentoApiCall(taxRequest, customerToken);
        }
        const orderId = await createOrderMagentoApiCall(orderRequest.order, customerToken);
        createOrderResponse = { orderId }
      } else if (guestCheckoutPage) {
        const isTunnelTaxSupported = isTunnelTaxSupportedCountry(getProductCountryCode(state));
        if(isTunnelTaxSupported) {
          const taxRequest = paymentTaxMagentoRequest(getBillingAddress(state)!, getBillingAddress(state)!, getShippingMethod(state));
          await calculateTaxMagentoApiCallGuestCheckout(taxRequest, getCartIdFromURL())
        }
        const paymentInfoGuestCheckout = {...orderRequest.order, email: getGuestCheckoutEmail(store.getState() as StoreState)!}
        const orderId = await createOrderMagentoApiCallGuestCheckout(paymentInfoGuestCheckout, getCartIdFromURL());
        createOrderResponse = { orderId }
      } else {
        throw new Error('Customer token is not present');
      }
    } else {
      // [SHOP-API-REMOVAL][WECOMM-20796] Shop createOrder path (/store/api/v1/checkout/.../payment/order)
      // is removed. All callers should build a MagentoOrderRequest (paymentMethod-shaped).
      // If we hit this branch, something is still constructing the old Shop OrderRequest.
      console.error(
        '[SHOP-API-REMOVAL][WECOMM-20796] createOrder thunk: Shop OrderRequest received. ' +
        'The Shop /store/api/v1/checkout/.../payment/order endpoint has been removed. ' +
        'orderRequest=%o',
        orderRequest
      );
      throw new Error('createOrder: MagentoOrderRequest (paymentMethod) required');
    }

    checkCJECookieForRemoval();
    return createOrderResponse;
  } catch(error) {
    const apiError = error as ApiError;
    const cartId = getCart(store.getState() as StoreState)?.cartId;
    const message: Message = {
      message: MessageTemplateId.ORDER_CONFIRMATION_ERROR,
      type: MessageType.primary,
    };

    if (cartId && apiError.responseStatus === 504) {
      store.dispatch(updateOrderStatusReprocess(false));
      store.dispatch(resetOrderStatusFailureCount());
      return orderStatusApiCall(cartId);
    }

    if (apiError && apiError.response && apiError.response.message) {
      message.message = apiError.response.message;
    }

    // RMS insert errors redirect to the pending confirmation page via OrderCreator.
    // Do not flash a payment failure alert before that redirect.
    if (message.message !== MessageTemplateId.RMS_ORDER_INSERT_ERROR) {
      thunkAPI.dispatch(updateGlobalMessage({
        id: PAYMENT_MESSAGE_ID,
        ...message
      }));
    }
    thunkAPI.dispatch(resetPaymentDetails());

    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    return state.shopCheckoutState.orderCreation.status !== ActionStatus.loading;
  })
});

export const orderStatusApiCall = async (cartId: string) => {
  const state = store.getState() as StoreState;
  const orderResultProcessor = new OrderResultProcessor();

  await store
          .dispatch(orderStatus(cartId))
          .then((res) => {
              const orderStatusResponse: OrderStatusResponse = res?.payload;
              const orderId: string = `${orderStatusResponse?.content?.order_id}`;

              switch(orderStatusResponse?.content?.status) {
                case OrderStatus.SUCCESS:
                  orderId && orderResultProcessor.processSuccess(orderId);
                  break;
                case OrderStatus.PENDING:
                  if (getOrderStatusFailureCount(state) === 0) {
                    store.dispatch(updateOrderStatusReprocess(true));
                  }

                  store.dispatch(updateOrderStatusFailureCount());

                  if (getOrderStatusFailureCount(state) < 2) {
                    setTimeout(async() => {
                      await orderStatusApiCall(cartId);
                    }, 5000);
                  }
                  break;
                default:
                  break;
              }
          })
          .catch((error) => {
              console.log("failed to create order status", error);
          });
}

export const orderStatus = createAsyncThunk('shopcheckout/orderStatus', async(cartId: string, thunkAPI) => {
  try {
    return await getOrderStatusMagentoApiCall(cartId);
  } catch (error) {
    thunkAPI.dispatch(resetPaymentDetails());
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    return state.shopCheckoutState.orderStatusCheck.status !== ActionStatus.loading;
  })
});

function checkCJECookieForRemoval() {
  if (isCjeCookiePresent()) {
    removeCookie(COOKIE_CJE);
  }
}

export const calculateFreight = createAsyncThunk('shopcheckout/calculateFreight', async(_data: {promoCodeToApply?: string} | undefined, thunkAPI) => {
  try{
    const state = thunkAPI.getState() as StoreState;
    const address = getShippingAddress(thunkAPI.getState() as StoreState)!;

    const freightCalculationRequest = freightCalculationMagentoRequest(address);
    const customerToken = getCustomerToken(state);

    if (isGuestCheckoutPage()) {
      return await calculateFreightMagentoApiCallGuestCheckout(freightCalculationRequest, getCartIdFromURL())
    } else {
      return await calculateFreightMagentoApiCall(freightCalculationRequest, customerToken);
    }
  } catch(error) {
    thunkAPI.dispatch(updateGlobalMessage({
      id: PAYMENT_MESSAGE_ID,
      message: GENERIC_TRANSACTION_ERROR,
      type: MessageType.primary
    }));
    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    return !state.shopCheckoutState.skipFreightCalculation || state.shopCheckoutState.freightCalculation.status !== ActionStatus.loading;
  })
});

export const getBraintreeClientToken = createAsyncThunk('shopcheckout/getBraintreeClientToken', async(clientTokenRequest: ClientTokenRequest, thunkAPI) => {
  try{

    if(isGuestCheckoutPage()) {
      const cartId = getCartIdFromURL()
      return await getBraintreeClientTokenGuestCheckoutApiCall(clientTokenRequest, cartId);
    }

    const customerToken = getCustomerToken(thunkAPI.getState() as StoreState);
    return await getBraintreeClientTokenMagentoApiCall(clientTokenRequest, customerToken);
  } catch(error) {
    thunkAPI.dispatch(updateGlobalMessage({
      id: PAYMENT_MESSAGE_ID,
      message: GENERIC_TRANSACTION_ERROR,
      type: MessageType.primary
    }));
    return thunkAPI.rejectWithValue(error);
  }
},{
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    return state.shopCheckoutState.clientToken.status !== ActionStatus.loading;
  })
});

export const calculateTax = createAsyncThunk('shopcheckout/calculateTax', async(_data: {promoCodeToApply: string} | undefined, thunkAPI) => {
  try {
    const state = thunkAPI.getState() as StoreState;
    const shippingMethod = getShippingMethod(state);
    const billingAddress = getBillingAddress(state);
    const shippingAddress = getShippingAddress(state);
    const shippable = (thunkAPI.getState() as StoreState).shopCartState?.getShopCart?.data?.shippable || false;

    const customerToken = getCustomerToken(state);
    if(isTunnelPurchaseFlow()) {
      const freightCalculationRequest = freightCalculationMagentoRequest(billingAddress!);
      if (isGuestCheckoutPage()) {
        await calculateFreightMagentoApiCallGuestCheckout(freightCalculationRequest, getCartIdFromURL())
      } else {
        await calculateFreightMagentoApiCall(freightCalculationRequest, customerToken);
      }
    }
    // shipping-information for tax api will not call for taxable countries (SPAIN, COLOMBIA, MEXICO) instead calling it on place order
    const isTunnelTaxSupported = isTunnelTaxSupportedCountry(getProductCountryCode(state));
    if(!isTunnelTaxSupported) {
      const taxRequest = paymentTaxMagentoRequest(shippable ? shippingAddress! : billingAddress!, billingAddress!, shippingMethod);
      if (isGuestCheckoutPage()) {
        await calculateTaxMagentoApiCallGuestCheckout(taxRequest, getCartIdFromURL())
      } else {
        await calculateTaxMagentoApiCall(taxRequest, customerToken);
      }
    }

    let totals;
    if (isGuestCheckoutPage()) {
      totals = await getLoggedInCartItemsTotalsMagentoApiCallGuestCheckout(getCartIdFromURL());
    } else {
      totals = await getLoggedInCartItemsTotalsMagentoApiCall(customerToken);
    }
    const taxInfo = mapMagentoTaxResponse(totals);
    return taxInfo;
  } catch(error: any) {
    let errorMessage = GENERIC_TRANSACTION_ERROR;
    if(error.response && error.response.code=='TAX_CALCULATION_ERR_003'){
      errorMessage = error.response.message;
	}

	thunkAPI.dispatch(updateGlobalMessage({
      id: PAYMENT_MESSAGE_ID,
      message: errorMessage,
      type: MessageType.primary
    }));

    return thunkAPI.rejectWithValue(error);
  }
}, {
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    return state.shopCheckoutState.taxCalculation.status !== ActionStatus.loading;
  })
});

/*----- CREATE INITIAL STATE ------*/

export const shopCheckoutStateInitialState: ShopCheckoutState = {
  skipFreightCalculation: false,
  activeStepId: undefined,
  disableNextStepButton: false,
  isComplete: false,
  checkoutInfoSubmitted: false,
  shippingAddress: undefined,
  freightCalculation: defaultApiState,
  orderCreation: defaultApiState,
  orderStatusCheck: defaultApiState,
  enableOrderStatusReprocess: false,
  orderStatusFailureCount: 0,
  selectedShippingMethod: undefined,
  selectedPaymentType: undefined,
  clientToken: defaultApiState,
  braintreeHostedFieldsStatus: undefined,
  paymentDetails: undefined,
  lastPaypalDetail: undefined,
  processingPaypalInfo: false,
  orderCompletion: undefined,
  taxCalculation: defaultApiState,
  billingAddress: undefined,
  applePayBillingAddress: undefined,
  googlePayBillingAddress: undefined,
  paypalBillingAddress: undefined,
  billingAddressSameAsShippingAddress: false,
  isShippingAddressFromAddressbook: false,
  currencyCode: undefined,
  countryCode: undefined,
  poOrderNumber: undefined,
  removedSavedCard: defaultApiState,
  editBillingAddressMode : false,
  taxInfo: undefined,
  removedPaymentMethod: undefined,
  purchaseOrderSelected: false,
  purchaseOrderApplied: false,
  purchaseOrderDetails: undefined,
  savePurchaseOrder: defaultApiState
};

/*----- CREATE STORE SLICE ------*/
export const shopCheckoutSlice = createSlice({
  name: "shopCheckout",
  initialState: shopCheckoutStateInitialState,
  reducers:{
    invalidateFreightResponse(state) {
      state.freightCalculation.data = undefined;
      state.freightCalculation.status = ActionStatus.idle;
    },
    updateSkipFreightCalculation(state, action){
      state.skipFreightCalculation = action.payload
    },
    updateActiveStep(state, action){
      state.activeStepId = action.payload;
    },
    updateDisableNextStepButton(state, action) {
      state.disableNextStepButton = action.payload;
    },
    updateIsCheckoutComplete(state, action) {
      state.isComplete = action.payload;
    },
    updateCheckoutInfoSubmitted(state, action){
      state.checkoutInfoSubmitted = action.payload;
    },
    updateShippingAddress(state, action){
      state.shippingAddress = updateAddressBeforeSave(action.payload);
    },
    updateIsShippingAddressFromAddressbook(state, action){
      state.isShippingAddressFromAddressbook = action.payload;
    },
    updateBillingAddress(state, action){
      state.billingAddress = updateAddressBeforeSave(action.payload);
    },
    updateApplePayBillingAddress(state, action) {
      state.applePayBillingAddress = updateAddressBeforeSave(action.payload);
    },
    updateGooglePayBillingAddress(state, action) {
      state.googlePayBillingAddress = updateAddressBeforeSave(action.payload);
    },
    updatePaypalBillingAddress(state, action) {
      state.paypalBillingAddress = updateAddressBeforeSave(action.payload);
    },
    updateSelectedShippingMethod(state, action){
      const selectedShippingMethodCode = action.payload;
      const freightCalculationResponse: RmsFreightCalcResponse = state.freightCalculation.data!;
      const selectedShippingMethodDetails = find(freightCalculationResponse.freightEstimateTypes, (freightEstimate: RmsFreightEstimateType) =>{
        return freightEstimate.shippingMethodCode === selectedShippingMethodCode;
      })!;
      const selectedShippingMethod: SelectedShippingMethod = {
        information: freightCalculationResponse.information,
        wareHouse: freightCalculationResponse.wareHouse,
        freightEstimateTypes: selectedShippingMethodDetails
      }
      state.selectedShippingMethod = selectedShippingMethod;
    },
    updateSelectedPaymentType(state, action){
      state.selectedPaymentType = action.payload;
    },
    shippingMethodSelected(state, action){
      state.selectedShippingMethod = action.payload;
    },
    resetPaymentDetails(state, action) {
      action.payload ? state.removedPaymentMethod = action.payload : state.removedPaymentMethod = undefined;
      state.paymentDetails = undefined;
    },
    updatePaymentDetails(state, action){
      state.paymentDetails = action.payload;
    },
    updatelastPaypalDetail(state, action){
      state.lastPaypalDetail = action.payload;
    },
    updateBillingAddressSameAsShippingAddressFlag(state, action){
      state.billingAddressSameAsShippingAddress = action.payload;
    },
    updateCountryCodeCurrencyCode(state,action){
      state.currencyCode = action.payload.currencyCode;
      state.countryCode = action.payload.countryCode;
    },
    updatePoOrderNumber(state,action) {
      state.poOrderNumber = action.payload;
    },
    updateVaultedCards(state, action) {
      state.clientToken.data!.vaultedCards = action.payload;
    },
    updateBraintreeHostedFieldsStatus(state, action) {
      state.braintreeHostedFieldsStatus = action.payload;
    },
    updateRemovedCardApiStatus(state, action) {
      state.removedSavedCard.status = action.payload;
    },
    updateBraintreeClientTokenApiStatus(state, action) {
      state.clientToken.status = action.payload;
    },
    updateBraintreeClientTokenData(state, action) {
      state.clientToken.data = action.payload;
    },
    updateOrderCreationApiStatus(state, action) {
      state.orderCreation.status = action.payload;
    },
    updateFreightCalculationApiStatus(state, action) {
      state.freightCalculation.status = action.payload;
    },
    updateTaxCalculationApiStatus(state, action) {
      state.taxCalculation.status = action.payload;
    },
    updateEditBillingAddressEditMode(state, action) {
      state.editBillingAddressMode = action.payload;
    },
    updateProcessingPaypalInfo(state,action) {
      state.processingPaypalInfo = action.payload;
    },
    calculateIntlTaxAmount(state, action) {
      state.taxCalculation.data = action.payload;
    },
    updateIntlTotalTax(state, action) {
      if (state.taxCalculation.data) {
        state.taxCalculation.data.totalTax = action.payload;
      } else {
        state.taxCalculation.data = {} as any;
        state.taxCalculation.data!.totalTax = action.payload;
      }
    },
    updateTaxInfo(state,action) {
      state.taxInfo = action.payload;
    },
    updateOrderStatusReprocess(state, action) {
      state.enableOrderStatusReprocess = action.payload
    },
    updateOrderStatusFailureCount(state) {
      state.orderStatusFailureCount = state.orderStatusFailureCount + 1;
    },
    resetOrderStatusFailureCount(state) {
      state.orderStatusFailureCount = 0;
    },
    updatePurchaseOrderSelected(state, action) {
      state.purchaseOrderSelected = action.payload;
    },
    updatePurchaseOrderApplied(state, action) {
      state.purchaseOrderApplied = action.payload;
    },
    updatePurchaseOrderDetails(state, action) {
      state.purchaseOrderDetails = action.payload;
    },
  },
  extraReducers(builder){
    builder
    .addCase(calculateFreight.pending, (state) => {
      state.freightCalculation.status = ActionStatus.loading;
    })
    .addCase(calculateFreight.fulfilled, (state, action) => {
      state.freightCalculation.status = ActionStatus.succeeded;
      state.freightCalculation.data = action.payload as any;
    })
    .addCase(calculateFreight.rejected, (state, action) => {
      state.freightCalculation.status = ActionStatus.failed;
      state.freightCalculation.error = action.payload;
    })
    .addCase(getBraintreeClientToken.pending, (state) => {
      state.clientToken.status = ActionStatus.loading;
    })
    .addCase(getBraintreeClientToken.fulfilled, (state, action) =>{
      state.clientToken.status = ActionStatus.succeeded;
      state.clientToken.data = action.payload;
    })
    .addCase(getBraintreeClientToken.rejected, (state, action) => {
      state.clientToken.status = ActionStatus.failed;
      state.clientToken.error = action.payload;
    })
    .addCase(calculateTax.pending, (state) => {
      state.taxCalculation.status = ActionStatus.loading;
    })
    .addCase(calculateTax.fulfilled, (state, action) => {
      state.taxCalculation.status = ActionStatus.succeeded;
      state.taxCalculation.data = action.payload;
    })
    .addCase(calculateTax.rejected, (state, action) => {
      state.taxCalculation.status = ActionStatus.failed;
      state.taxCalculation.error = action.payload;
    })
    .addCase(createOrder.pending, (state) => {
      state.orderCreation.status = ActionStatus.loading;
    })
    .addCase(createOrder.fulfilled, (state, action) => {
      state.orderCreation.status = ActionStatus.succeeded;
      state.orderCreation.data = action.payload;
    })
    .addCase(createOrder.rejected, (state, action) => {
      state.orderCreation.status = ActionStatus.failed;
      state.orderCreation.error = action.payload;
    })
    .addCase(orderStatus.pending, (state) => {
      state.orderStatusCheck.status = ActionStatus.loading;
    })
    .addCase(orderStatus.fulfilled, (state, action) => {
      state.orderStatusCheck.status = ActionStatus.succeeded;
      state.orderStatusCheck.data = action.payload;
    })
    .addCase(orderStatus.rejected, (state, action) => {
      state.orderStatusCheck.status = ActionStatus.failed;
      state.orderStatusCheck.error = action.payload;
    })
    .addCase(removeSavedCard.pending, (state) => {
      state.removedSavedCard.status = ActionStatus.loading;
    })
    .addCase(removeSavedCard.fulfilled, (state) => {
      state.removedSavedCard.status = ActionStatus.succeeded;
    })
    .addCase(removeSavedCard.rejected, (state, action) => {
      state.removedSavedCard.status = ActionStatus.failed;
      state.removedSavedCard.error = action.payload;
    })
    .addCase(savePurchaseOrder.pending, (state) => {
      state.savePurchaseOrder.status = ActionStatus.loading;
    })
    .addCase(savePurchaseOrder.fulfilled, (state) => {
      state.savePurchaseOrder.status = ActionStatus.succeeded;
    })
    .addCase(savePurchaseOrder.rejected, (state, action) => {
      state.savePurchaseOrder.status = ActionStatus.failed;
      state.savePurchaseOrder.error = action.payload;
    })
  }
});

/*----- CREATE SLICE ACTIONS ------*/


export const {
  updateApplePayBillingAddress,
  updateGooglePayBillingAddress,
  updatePaypalBillingAddress,
  invalidateFreightResponse,
  updateSkipFreightCalculation,
  updateTaxCalculationApiStatus,
  updateFreightCalculationApiStatus,
  updateOrderCreationApiStatus,
  updateBraintreeClientTokenApiStatus,
  updateBraintreeClientTokenData,
  updateRemovedCardApiStatus,
  updateActiveStep,
  updateDisableNextStepButton,
  updateIsCheckoutComplete,
  updateShippingAddress,
  updateIsShippingAddressFromAddressbook,
  shippingMethodSelected,
  resetPaymentDetails,
  updatePaymentDetails,
  updatelastPaypalDetail,
  updateBillingAddress,
  updateBillingAddressSameAsShippingAddressFlag,
  updateCountryCodeCurrencyCode,
  updatePoOrderNumber,
  updateVaultedCards,
  updateEditBillingAddressEditMode,
  updateCheckoutInfoSubmitted,
  updateSelectedShippingMethod,
  updateSelectedPaymentType,
  updateProcessingPaypalInfo,
  updateBraintreeHostedFieldsStatus,
  calculateIntlTaxAmount,
  updateIntlTotalTax,
  updateTaxInfo,
  updateOrderStatusReprocess,
  updateOrderStatusFailureCount,
  resetOrderStatusFailureCount,
  updatePurchaseOrderSelected,
  updatePurchaseOrderApplied,
  updatePurchaseOrderDetails
} = shopCheckoutSlice.actions

/*----- CREATE SELECTORS ------*/

export const validPaymentDetails =  (state: StoreState): boolean => {
  if (!state.shopCheckoutState.paymentDetails) {
    return false;
  } else if (state.shopCheckoutState.paymentDetails?.paymentType === PaymentType.BRAINTREE_CREDIT_CARD) {
    return !! state.shopCheckoutState.paymentDetails?.paymentNonce;
  } else if (state.shopCheckoutState.paymentDetails?.paymentType === PaymentType.BRAINTREE_PAYPAL) {
    return !! state.shopCheckoutState.paymentDetails?.paymentNonce;
  } else if (state.shopCheckoutState.paymentDetails?.paymentType === PaymentType.PURCHASE_ORDER) {
    return !! state.shopCheckoutState.poOrderNumber;
  } else if (state.shopCheckoutState.paymentDetails?.paymentType === PaymentType.BRAINTREE_IDEAL) {
    return !!state.shopCheckoutState.paymentDetails?.paymentNonce && !!state.shopCheckoutState.billingAddress;
  } else {
    return true;
  }
}

export const skipFreightCalculation = (state: StoreState) =>
state.shopCheckoutState.skipFreightCalculation;

export const getActiveStepId = (state: StoreState) =>
state.shopCheckoutState.activeStepId;

export const isNextStepButtonDisabled = (state: StoreState) =>
state.shopCheckoutState.disableNextStepButton;

export const isCheckoutComplete = (state: StoreState) =>
state.shopCheckoutState.isComplete;

export const getFreightCalculationResponse = (state: StoreState) =>
state.shopCheckoutState.freightCalculation.data;

export const getFreightCalculationStatus = (state: StoreState) =>
state.shopCheckoutState.freightCalculation.status;

export const getFreightCalculationError = (state: StoreState) =>
state.shopCheckoutState.freightCalculation.error;

export const getShippingAddress = (state: StoreState) =>
state.shopCheckoutState.shippingAddress;

export const isShippingAddressFromAddressbook = (state: StoreState) =>
state.shopCheckoutState.isShippingAddressFromAddressbook;

export const isShippingAddressValid = (state: StoreState) =>
getShippingAddress(state)?.verificationStatus || getShippingAddress(state)?.dpvMatchCode != AddressMatchCode.Invalid;

export const getShippingMethod = (state: StoreState) =>
state.shopCheckoutState.selectedShippingMethod;

export const getClientTokenResponse = (state: StoreState) =>
state.shopCheckoutState.clientToken.data;

export const getClientTokenResponseError = (state: StoreState) =>
state.shopCheckoutState.clientToken.error;

export const getPaymentDetails = (state: StoreState) =>
state.shopCheckoutState.paymentDetails;

export const getRemovedPaymentMethod = (state: StoreState) =>
state.shopCheckoutState.removedPaymentMethod;

export const getPaymentMethod = (state: StoreState) =>
state.shopCheckoutState.paymentDetails?.paymentType;

export const getlastPaypalDetail = (state: StoreState) =>
state.shopCheckoutState.lastPaypalDetail;

export const getBillingAddress = (state: StoreState) =>
state.shopCheckoutState.billingAddress;

export const getApplePayBillingAddress = (state: StoreState) =>
state.shopCheckoutState.applePayBillingAddress;

export const getGooglePayBillingAddress = (state: StoreState) =>
state.shopCheckoutState.googlePayBillingAddress;

export const getPaypalBillingAddress = (state: StoreState) =>
state.shopCheckoutState.paypalBillingAddress;

export const isBillingAddressSameAsShippingAddress = (state: StoreState) =>
state.shopCheckoutState.billingAddressSameAsShippingAddress;

export const getCurrencyCode = (state: StoreState) =>
state.shopCheckoutState.currencyCode;

export const getCountryCode = (state: StoreState) =>
state.shopCheckoutState.countryCode;

export const getTaxCalculation = (state: StoreState) =>
state.shopCheckoutState.taxCalculation.data?.totalTax;

export const getTotalTax = (state: StoreState) =>
getTaxCalculation(state)?.amount;

export const getDigitalHandlingFees = (state: StoreState) =>
 state.shopCheckoutState.taxCalculation.data?.digitalHandlingFees;

export const getDigitalHandlingFeesAmount = (state: StoreState) => getDigitalHandlingFees(state)?.amount || 0;

export const getDigitalHandlingFeesIsbn = (state: StoreState) =>
state.shopCheckoutState.taxCalculation.data?.digitalHandlingFeesIsbn;

export const getPoOrderNumber = (state: StoreState) =>
state.shopCheckoutState.poOrderNumber;

export const getBraintreeHostedFieldsStatus = (state: StoreState) =>
state.shopCheckoutState.braintreeHostedFieldsStatus;

export const getRemoveCreditCardStatus = (state: StoreState) =>
state.shopCheckoutState.removedSavedCard.status;

export const getRemoveCreditCardError = (state: StoreState) =>
state.shopCheckoutState.removedSavedCard.error;

export const getBraintreeClientTokenApiStatus = (state: StoreState) =>
state.shopCheckoutState.clientToken.status;

export const getOrderCreationStatus = (state: StoreState) =>
state.shopCheckoutState.orderCreation.status;

export const getOrderCreationError = (state: StoreState) =>
state.shopCheckoutState.orderCreation.error;

export const getOrderId = (state: StoreState) =>
state.shopCheckoutState.orderCreation.data?.orderId;

export const getTaxCalculationStatus = (state: StoreState) =>
state.shopCheckoutState.taxCalculation.status;

export const getEditBillingAddressMode = (state: StoreState) =>
state.shopCheckoutState.editBillingAddressMode;

export const getSelectedShippingMethod = (state: StoreState) =>
state.shopCheckoutState.selectedShippingMethod;

export const getSelectedPaymentType = (state: StoreState) =>
state.shopCheckoutState.selectedPaymentType;

export const getOrderStatusCheck = (state: StoreState) =>
  state.shopCheckoutState.orderStatusCheck.status;

export const getOrderStatus = (state: StoreState) =>
state.shopCheckoutState.orderStatusCheck.data?.content.status;

export const getOrderStatusReprocess = (state: StoreState) =>
state.shopCheckoutState.enableOrderStatusReprocess;

export const getOrderStatusFailureCount = (state: StoreState) =>
state.shopCheckoutState.orderStatusFailureCount;

export const getSelectedShippingFreightPrice = (state: StoreState) => {
  const freightPrice = getSelectedShippingMethod(state)?.freightEstimateTypes?.freightPrice?.value;
  return freightPrice ? freightPrice : 0;
}

export const getSelectedShippingHandlingPrice = (state: StoreState) => {
  const handlingPrice = getSelectedShippingMethod(state)?.freightEstimateTypes?.handlingPrice?.value;
  return handlingPrice ? handlingPrice : 0;
}

export const getSelectedShippingTotal = (state: StoreState) =>
getSelectedShippingFreightPrice(state) + getSelectedShippingHandlingPrice(state);

export const getTaxCalculationError = (state: StoreState) =>
state.shopCheckoutState.taxCalculation.error;

export const isCheckoutInfoSubmitted = (state: StoreState) =>
state.shopCheckoutState.checkoutInfoSubmitted;

export const isProcessingPaypalInfo = (state: StoreState) =>
state.shopCheckoutState.processingPaypalInfo;

export const isPurchaseOrderSelected = (state: StoreState) =>
state.shopCheckoutState.purchaseOrderSelected;

export const isPurchaseOrderApplied = (state: StoreState) =>
state.shopCheckoutState.purchaseOrderApplied;

export const getTotalPrice = (state: StoreState) =>
{
  const cart = state.shopCartState.getShopCart.data;
  let newTotal = cart?.totalAmount;

  return totalWithTaxAndServiceFeeCalculation(newTotal, cart, state);
}

// Will sum up the subtotal, tax, and service fee
export const getTotalBeforeDiscount = (state: StoreState) =>
  {
    const cart = state.shopCartState.getShopCart.data;
    let newTotal = cart?.subTotal;
  
    return totalWithTaxAndServiceFeeCalculation(newTotal, cart, state);
  }

function totalWithTaxAndServiceFeeCalculation(value, cart, state) {
  if (cart?.shippable) {
    const selectedShippingtotal = getSelectedShippingTotal(state);
    if (value && selectedShippingtotal) {
        value += selectedShippingtotal;
    }
  }

  const totalTax = getTotalTax(state);
  if (value && totalTax !== undefined && totalTax >= 0) {
      value += totalTax;
  } else {
    if (!isCheckoutInfoSubmitted(state)) {
      const estimatedTaxValue = state.estimatedTaxState.data?.totalTax?.amount;
      if (value && estimatedTaxValue) {
          value += estimatedTaxValue;
      }
    }
  }

  const digitalHandlingFees = getDigitalHandlingFeesAmount(state);
  if (value && digitalHandlingFees) {
      value += digitalHandlingFees;
  }

  if(value) {
   return Math.round(value*100)/100;
  }

  return value;
}

export const getTaxInfo = (state: StoreState) =>
state.shopCheckoutState.taxInfo;

/*----- CREATE REDUCER ------*/

export const shopCheckoutReducer = shopCheckoutSlice.reducer;

/*----- API CALLS ------*/

async function getBraintreeClientTokenMagentoApiCall(clientTokenRequest: ClientTokenRequest, token: string) {
  const url = _getBraintreeURL(clientTokenRequest);
  const response = await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
  );
  return mapBraintreeMagentoResponse(response);
}

function _getBraintreeURL(clientTokenRequest: ClientTokenRequest) {
  return getBraintreeUrlWithParams(clientTokenRequest, new URL(environment().magentoUrl + ShopApi.getBraintreeTokenMagento));
}

async function getBraintreeClientTokenGuestCheckoutApiCall(clientTokenRequest: ClientTokenRequest, cartId: string) {
  const url = _getBraintreeURLGuestCheckout(clientTokenRequest, cartId);
  const response = await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })
  );
  return mapBraintreeMagentoResponse(response);
}

function _getBraintreeURLGuestCheckout(clientTokenRequest: ClientTokenRequest, cartId: string) {
  return getBraintreeUrlWithParams(clientTokenRequest, new URL(environment().magentoUrl + GuestCheckoutAPI.braintree(cartId)));
}

function getBraintreeUrlWithParams(clientTokenRequest: ClientTokenRequest, url: URL) {
  const urlToChange = new URL(url);
  if (clientTokenRequest.businessUnit) {
    urlToChange.searchParams.append("businessUnit", clientTokenRequest.businessUnit);
  }

  if (clientTokenRequest.currencyCode) {
    urlToChange.searchParams.append("currencyCode", clientTokenRequest.currencyCode);
  }
  if (clientTokenRequest.countryCode) {
    urlToChange.searchParams.append("countryCode", clientTokenRequest.countryCode);
  }

  if (clientTokenRequest.type === "cart") {
    urlToChange.searchParams.append("getVaultedCards", 'false');
  } else if (clientTokenRequest.type === "checkout") {
    urlToChange.searchParams.append("getVaultedCards", 'true');
  }
  return urlToChange;
}

export async function createOrderMagentoApiCall(order: PaymentInformation, token: string) {
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  return await getResponseData(
    new Request(environment().magentoUrl + ShopApi.createPaymentInfo, {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(order)
    }));
}

async function createOrderMagentoApiCallGuestCheckout(order: PaymentInformationGuestCheckout, cartId: string) {
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const headers = {
    'Content-Type': 'application/json'
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  return await getResponseData(
    new Request(environment().magentoUrl + GuestCheckoutAPI.placeOrder(cartId), {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(order)
    }));
}

async function getOrderStatusMagentoApiCall(cartId: string) {
  const customerToken = getCustomerToken(store.getState() as StoreState);
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${customerToken}`
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  return await getResponseData(
    new Request(getOrderStatusUrlMagento(cartId), {
      method: 'GET',
      credentials: 'include',
      headers: headers,
    }));
}

async function removeVaultedCardApiCallMagento(removeCreditCardRequest: RemoveCreditCardRequest, token: string){
  const url = new URL(environment().magentoUrl + ShopApi.deleteVaultedCardMagento.replace(":creditCardUniqueId", removeCreditCardRequest.creditCardUniqueId));
  return await getResponseData(
    new Request(url.toString(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
    })
  );
}

async function calculateFreightMagentoApiCall(requestBody: GetShippingMethodMagentoRequest, token: string) {
  const response = await getMagentoShippingMethodsApiRequest(requestBody, token)
  return mapMagentoShippingMethodsResponse(response);
}

export async function getMagentoShippingMethodsApiRequest(
  requestBody: GetShippingMethodMagentoRequest,
  token: string
) {
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const url = new URL(environment().magentoUrl + ShopApi.getShippingMethodsMagento);
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  return await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(requestBody)
    })
  );
}

async function calculateFreightMagentoApiCallGuestCheckout(requestBody: GetShippingMethodMagentoRequest, cartId: string){
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const url = new URL(environment().magentoUrl + GuestCheckoutAPI.estimateShippingMethod(cartId));
  const headers = {
    'Content-Type': 'application/json'
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  const response = await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(requestBody)
    })
  );
  return mapMagentoShippingMethodsResponse(response);
}

export async function calculateTaxMagentoApiCall(taxRequest: SetShippingMethodMagentoRequest, token: string) {
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const url = new URL(environment().magentoUrl + ShopApi.setShippingMethodsMagento);
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  const response = await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(taxRequest)
    })
  );
  return response;
}

async function calculateTaxMagentoApiCallGuestCheckout(taxRequest: SetShippingMethodMagentoRequest, cartId: string) {
  const recaptchaToken = await getRecaptchaToken(RecaptchaAction.payment);
  const url = new URL(environment().magentoUrl + GuestCheckoutAPI.shippingInformation(cartId));
  const headers = {
    'Content-Type': 'application/json',
  }

  if (recaptchaToken) {
    headers[MAGENTO_RECAPTCHA_HEADER] = recaptchaToken;
  }

  const response = await getResponseData(
    new Request(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers: headers,
      body: JSON.stringify(taxRequest)
    })
  );
  return response;
}

export const recalculateTax = async (state: StoreState, promoCode: string, thunkAPI: any) => {
  const getEstimatedTaxParams: GetEstimatedTaxParams = {};

  const shippingAddress = getShippingAddress(state);
  const zipCode = getZipCode(state);
  const estimatedTaxStatus = getEstimatedTaxStatus(state);
  const isCheckoutReviewStep = isCheckoutInfoSubmitted(state);

  if (zipCode || estimatedTaxStatus === ActionStatus.loading) {
    if (estimatedTaxStatus === ActionStatus.loading) {
      await waitForEstimatedTax();
    }

    getEstimatedTaxParams.zipCode = zipCode;
    getEstimatedTaxParams.promoCode = promoCode;
    thunkAPI.dispatch(getEstimatedTax(getEstimatedTaxParams));
  }

  if (shippingAddress) {
    thunkAPI.dispatch(updateSkipFreightCalculation(false));
    await thunkAPI.dispatch(calculateFreight({promoCodeToApply: promoCode}));
  }

  if (isCheckoutReviewStep || isCheckoutComplete(state)) {
    thunkAPI.dispatch(calculateTax({promoCodeToApply: promoCode}));
  }
}
